import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { collection, getDocs } from 'firebase/firestore'
import {
  Box,
  Flex,
  Heading,
  Text,
  Image,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react'
import { db } from '../config/firebase'
import { getYouTubeId } from '../utils/youtubeUtils'
import { useSettings } from '../contexts/SettingsContext'
import logoImg from '../assets/images/logo_flag.png'
import { Footer } from '../components/Footer'

const VIDEOS_COLLECTION = 'videos'

const YouTubePage = () => {
  const { settings } = useSettings() as {
    settings: { playbackMode?: 'manual' | 'auto' }
  }
  const [videos, setVideos] = useState<
    Array<{
      id: string
      name?: string
      youtubeUrl: string
      videoId?: string
      order?: number
    }>
  >([])
  const [loading, setLoading] = useState(true)
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()

  useEffect(() => {
    const fetchVideos = async () => {
      if (!db) {
        setLoading(false)
        return
      }
      try {
        const snapshot = await getDocs(collection(db, VIDEOS_COLLECTION))
        const list = snapshot.docs
          .map((d) => ({
            id: d.id,
            name: d.data().name,
            youtubeUrl: d.data().youtubeUrl,
            videoId: d.data().videoId,
            order: d.data().order ?? 999999,
          }))
          .sort((a, b) => (a.order ?? 999999) - (b.order ?? 999999))
        setVideos(list)
      } catch (err) {
        console.error('비디오 로드 실패:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchVideos()
  }, [])

  const displayVideos = videos.map((video, index) => ({
    id: video.id,
    name: video.name ?? `비디오 ${index + 1}`,
    url: video.youtubeUrl,
    videoId: video.videoId ?? getYouTubeId(video.youtubeUrl),
  }))

  const handleVideoClick = (url: string) => {
    const videoId = getYouTubeId(url)
    if (videoId) {
      setSelectedVideoId(videoId)
      onOpen()
    } else {
      alert('유효하지 않은 유튜브 URL입니다.')
    }
  }

  const handleModalClose = () => {
    setSelectedVideoId(null)
    onClose()
  }

  const embedSrc = selectedVideoId
    ? (() => {
        const params = new URLSearchParams()
        params.set('rel', '0')
        if (settings?.playbackMode === 'auto') {
          params.set('autoplay', '1')
          params.set('mute', '1')
        }
        return `https://www.youtube-nocookie.com/embed/${selectedVideoId}?${params.toString()}`
      })()
    : ''

  const HeaderContent = () => (
    <Flex flexDirection="column" alignItems="center" gap={2}>
      <Box
        w="40px"
        h="40px"
        borderRadius="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.200"
      >
        <Image
          src={logoImg}
          alt="EXCELLO"
          w="28px"
          h="28px"
          objectFit="contain"
        />
      </Box>
      <Heading
        as="h2"
        size="lg"
        letterSpacing="0.1em"
        color="gray.900"
        textTransform="uppercase"
      >
        EXCELLO
      </Heading>
    </Flex>
  )

  const BackLink = () => (
    <Flex mt={12} justifyContent="center">
      <Link to="/">
        <Flex
          alignItems="center"
          gap={2}
          color="gray.500"
          fontWeight="medium"
          fontSize="sm"
          _hover={{ color: 'blue.500' }}
        >
          <Box as="span" className="material-symbols-outlined" fontSize="xl">
            arrow_back
          </Box>
          Back to main portal
        </Flex>
      </Link>
    </Flex>
  )

  if (loading) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
        <Box
          as="header"
          position="sticky"
          top={0}
          zIndex={50}
          w="100%"
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Flex maxW="1280px" mx="auto" py={4} px={6} justifyContent="center">
            <HeaderContent />
          </Flex>
        </Box>
        <Box as="main" flex={1} pb={24} px={6}>
          <Text textAlign="center" color="gray.500" fontSize="lg" py={12}>
            비디오를 불러오는 중...
          </Text>
          <BackLink />
        </Box>
        <Footer />
      </Box>
    )
  }

  if (displayVideos.length === 0) {
    return (
      <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
        <Box
          as="header"
          position="sticky"
          top={0}
          zIndex={50}
          w="100%"
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Flex maxW="1280px" mx="auto" py={4} px={6} justifyContent="center">
            <HeaderContent />
          </Flex>
        </Box>
        <Box as="main" flex={1} pb={24} px={6}>
          <Text textAlign="center" color="gray.500" fontSize="lg" py={12}>
            등록된 비디오가 없습니다.
          </Text>
          <BackLink />
        </Box>
        <Footer />
      </Box>
    )
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="gray.50">
      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={50}
        w="100%"
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Flex maxW="1280px" mx="auto" py={4} px={6} justifyContent="center">
          <HeaderContent />
        </Flex>
      </Box>

      <Box as="main" flex={1} pb={24} px={6}>
        <Box maxW="1280px" mx="auto" pt={16} pb={12} textAlign="center">
          <Heading
            as="h1"
            size={{ base: 'xl', md: '2xl' }}
            color="gray.900"
            mb={4}
          >
            Official Videos
          </Heading>
          <Box w={20} h={1} bg="blue.500" mx="auto" borderRadius="full" />
        </Box>

        <Box maxW="1280px" mx="auto">
          <SimpleGrid
            columns={{ base: 1, md: 2, xl: 3 }}
            gap={8}
            maxW={{ base: '400px', md: 'none' }}
            mx={{ base: 'auto', md: 0 }}
          >
            {displayVideos.map((video) => (
              <Box
                key={video.id}
                as="button"
                display="flex"
                flexDirection="column"
                h="320px"
                textAlign="left"
                onClick={() => handleVideoClick(video.url)}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                boxShadow="sm"
                overflow="hidden"
                cursor="pointer"
                transition="all 0.3s"
                _hover={{
                  boxShadow: 'xl',
                  '& .yt-play-overlay': { opacity: 1 },
                  '& .yt-thumb': { transform: 'scale(1.05)' },
                }}
              >
                <Box
                  position="relative"
                  h="200px"
                  overflow="hidden"
                  flexShrink={0}
                >
                  {video.videoId ? (
                    <Box
                      className="yt-thumb"
                      w="100%"
                      h="100%"
                      backgroundImage={`url(https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg)`}
                      backgroundSize="cover"
                      backgroundPosition="center"
                      transition="transform 0.5s"
                    />
                  ) : (
                    <Flex
                      w="100%"
                      h="100%"
                      bg="gray.800"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box
                        as="span"
                        className="material-symbols-outlined"
                        fontSize="3rem"
                        color="gray.400"
                      >
                        play_arrow
                      </Box>
                    </Flex>
                  )}
                  <Flex
                    className="yt-play-overlay"
                    position="absolute"
                    inset={0}
                    alignItems="center"
                    justifyContent="center"
                    bg="blackAlpha.400"
                    opacity={0}
                    transition="opacity 0.3s"
                  >
                    <Flex
                      w={14}
                      h={14}
                      borderRadius="full"
                      bg="blue.600"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                    >
                      <Box
                        as="span"
                        className="material-symbols-outlined"
                        fontSize="2rem"
                      >
                        play_arrow
                      </Box>
                    </Flex>
                  </Flex>
                </Box>
                <Flex p={5} flex={1} alignItems="center">
                  <Text
                    as="h3"
                    fontSize="lg"
                    fontWeight="bold"
                    color="gray.900"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {video.name}
                  </Text>
                </Flex>
              </Box>
            ))}
          </SimpleGrid>
        </Box>

        <BackLink />
      </Box>

      <Footer />

      <Modal isOpen={isOpen} onClose={handleModalClose} size="4xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="black" maxW="900px">
          <ModalCloseButton color="white" size="lg" />
          <ModalBody p={0}>
            <Box aspectRatio="16/9">
              <iframe
                title="Youtube Video"
                src={embedSrc}
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default YouTubePage
